'use client'

import React from 'react'
import Link from 'next/link'

/**
 * Affiliate helpers, currently Our Place (impact.com programme 19449, 10%,
 * 30-day cookie).
 *
 * Two things this exists to enforce:
 *
 * 1. DEEP LINKS. The bare tracking link redirects to fromourplace.com's
 *    homepage. Sending a reader who just read about a specific $249.95 set to
 *    a homepage and making them search for it is the single biggest avoidable
 *    conversion loss in an affiliate placement. Impact honours a `u` parameter
 *    on the short link and still stamps irclickid, so every link here should
 *    name its destination product.
 *
 * 2. subId1 ATTRIBUTION. Impact reports subId1 per click, so every placement
 *    passes one. Without it there is no way to learn which page or which
 *    position on a page actually earns, and no way to justify keeping or
 *    removing a placement later.
 */
const OUR_PLACE_TRACKING = 'https://our-place.pxf.io/3kODOB'

export const OUR_PLACE_PRODUCTS = {
  essentialsTrio: 'https://fromourplace.com/products/essentials-pan-trio-8pc',
  alwaysPan: 'https://fromourplace.com/products/always-essential-cooking-pan',
  cookwareSet: 'https://fromourplace.com/products/cookware-set',
} as const

/** Build a tracked, deep-linked Our Place URL. `subId` labels the placement. */
export function ourPlaceLink(destination?: string, subId?: string) {
  const params = new URLSearchParams()
  if (subId) params.set('subId1', subId)
  if (destination) params.set('u', destination)
  const query = params.toString()
  return query ? `${OUR_PLACE_TRACKING}?${query}` : OUR_PLACE_TRACKING
}

/** Outbound affiliate anchor. rel is non-negotiable: sponsored + nofollow. */
export function AffLink({
  href,
  children,
  className = 'text-[#1E6B47] hover:text-[#155235] underline font-medium',
}: {
  href: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <a href={href} rel="sponsored nofollow noopener" target="_blank" className={className}>
      {children}
    </a>
  )
}

/**
 * Compact disclosure line. FTC 16 CFR Part 255 requires disclosure to be clear,
 * conspicuous and proximate to the endorsement, so this belongs ABOVE the first
 * affiliate link on any page that carries one, not in a footer.
 */
export function AffDisclosureLine({ anchor }: { anchor?: string }) {
  return (
    <p className="not-prose my-4 text-xs text-gray-500">
      Contains affiliate links · we may earn a commission at no cost to you ·{' '}
      {anchor ? (
        <a href={`#${anchor}`} className="underline hover:text-gray-700">
          full disclosure
        </a>
      ) : (
        <Link href="/affiliate-disclosure" className="underline hover:text-gray-700">
          full disclosure
        </Link>
      )}
    </p>
  )
}

/**
 * Product card for the Essentials Pan Trio.
 *
 * Deliberately offers TWO destinations. For a considered $250 purchase, cold
 * traffic sent straight to a merchant converts badly; traffic that reads our
 * review first arrives pre-sold by the score, the coating chemistry and the
 * Consumer Reports PFAS screen, and converts far better. So the review is the
 * primary action and the merchant is the secondary one, and both are tracked
 * separately by subId so we can see which path actually earns.
 */
export function OurPlaceTrioCard({
  placement,
  showReviewLink = true,
}: {
  placement: string
  showReviewLink?: boolean
}) {
  const buyHref = ourPlaceLink(OUR_PLACE_PRODUCTS.essentialsTrio, `${placement}-buy`)
  const imgHref = ourPlaceLink(OUR_PLACE_PRODUCTS.essentialsTrio, `${placement}-image`)

  return (
    <div className="not-prose my-8 overflow-hidden rounded-xl border border-[#1E6B47]/20 bg-white">
      <div className="grid gap-0 sm:grid-cols-[minmax(0,240px)_1fr]">
        <a href={imgHref} rel="sponsored nofollow noopener" target="_blank" className="block bg-[#F7F5F2]">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/blog/our-place/trio-terracotta.jpg"
            alt="Our Place Essentials Pan Trio in Terracotta, three ceramic nonstick fry pans with lids and storage racks"
            loading="lazy"
            className="h-full w-full object-cover"
          />
        </a>

        <div className="p-5">
          <div className="flex items-baseline gap-3">
            <p className="font-display text-lg font-bold text-ink">
              Our Place Essentials Pan Trio
            </p>
            <span className="shrink-0 rounded-full bg-[#1E6B47]/10 px-2.5 py-0.5 text-xs font-bold text-[#1E6B47]">
              8.7 / 10
            </span>
          </div>

          <p className="mt-2 text-sm leading-relaxed text-gray-700">
            Our pick when you genuinely want nonstick. The Thermakind coating is
            sol-gel <strong>silica, not a fluoropolymer</strong>, so unlike PTFE
            there is no polymer present to shed as microplastic. Consumer
            Reports screened Our Place against 96 PFAS compounds in 2022 and
            found none.
          </p>

          <p className="mt-2 text-sm leading-relaxed text-gray-600">
            <strong>The catch:</strong> like every ceramic nonstick, treat it as
            a 2&ndash;3 year consumable rather than lifetime cookware.
          </p>

          <p className="mt-3 text-sm text-gray-500">
            8 pieces (8&Prime;, 10&Prime;, 12&Prime; + lids + storage) · $249.95
            · 100-day trial, free returns
          </p>

          <div className="mt-4 flex flex-wrap gap-3">
            {showReviewLink && (
              <Link
                href="/blog/our-place-essentials-pan-trio-review"
                className="rounded-control bg-[#1E6B47] px-4 py-2 text-sm font-semibold text-white hover:bg-[#155235] pressable"
              >
                Read our full review
              </Link>
            )}
            <AffLink
              href={buyHref}
              className="rounded-control border border-[#1E6B47]/40 px-4 py-2 text-sm font-semibold text-[#1E6B47] hover:bg-[#1E6B47]/5 no-underline pressable"
            >
              Check price at Our Place
            </AffLink>
          </div>
        </div>
      </div>
    </div>
  )
}
